/* eslint-disable complexity */
import { graphql } from 'relay-runtime';
import { parseRoute } from 'src/wac-routes';

import { customerLandingPageRelayQuery$data } from '@/__generated_contentful__/customerLandingPageRelayQuery.graphql';
import { WAC_TO_CPAPI_LOCALE_MAP } from '@/utils/content-queries/constants';

/**
 * Contentful Customer Landing query
 */
export const CustomerLandingPageRelayQuery = graphql`
	query customerLandingPageRelayQuery($locale: String!) {
		customerStoryCollection(
			locale: $locale
			limit: 200
			order: sys_publishedAt_DESC
			where: { sys: { publishedAt_exists: true }, storyTitleExternal_exists: true }
		) {
			items {
				sys {
					publishedAt
				}
				storyTitleExternal
				customerCompany {
					organizationNameExternal
					logoCollection(limit: 5) {
						items {
							description
							contentType
							url
						}
					}
					industryCollection(limit: 5) {
						items {
							industryName
						}
					}
					region {
						regionNameExternal
					}
					organizationSize {
						companySizeTitleExternal
					}
				}
				advocateQuotesCollection(limit: 5) {
					items {
						advocateName
						advocateQuote
						advocateJobTitle
						advocateHeadshot {
							description
							contentType
							url
						}
					}
				}
				storyIcon {
					description
					contentType
					url
				}
				callToActionCollection(limit: 5) {
					items {
						ctaText
						ctaUrl
					}
				}
				productCollection(limit: 20) {
					items {
						... on ProductListing {
							productNameReference {
								productName
							}
						}
					}
				}
				marketplaceAppsCollection(limit: 5) {
					items {
						title
					}
				}
				solutionCollection(limit: 5) {
					items {
						solutionNameExternal
					}
				}
				solutionPartnersCollection(limit: 5) {
					items {
						organizationNameExternal
					}
				}
				urlSlug
			}
		}
		taxonomyIndustryCollection(locale: $locale, limit: 20) {
			total
			items {
				industryName
				linkedFrom(
					allowedLocales: [
						"en-US"
						"es"
						"ja-JP"
						"pt-BR"
						"fr-FR"
						"de-DE"
						"nl-NL"
						"it-IT"
						"ko-KR"
						"pl-PL"
						"ru-RU"
						"zh-CN"
					]
				) {
					companyNameInternalCollection {
						total
						items {
							organizationNameExternal
							linkedFrom(
								allowedLocales: [
									"en-US"
									"es"
									"ja-JP"
									"pt-BR"
									"fr-FR"
									"de-DE"
									"nl-NL"
									"it-IT"
									"ko-KR"
									"pl-PL"
									"ru-RU"
									"zh-CN"
								]
							) {
								customerStoryCollection(limit: 1) {
									total
								}
							}
						}
					}
				}
			}
		}
		productNameCollection(locale: $locale, limit: 25) {
			total
			items {
				productName
				linkedFrom(
					allowedLocales: [
						"en-US"
						"es"
						"ja-JP"
						"pt-BR"
						"fr-FR"
						"de-DE"
						"nl-NL"
						"it-IT"
						"ko-KR"
						"pl-PL"
						"ru-RU"
						"zh-CN"
					]
				) {
					productListingCollection {
						total
						items {
							linkedFrom(
								allowedLocales: [
									"en-US"
									"es"
									"ja-JP"
									"pt-BR"
									"fr-FR"
									"de-DE"
									"nl-NL"
									"it-IT"
									"ko-KR"
									"pl-PL"
									"ru-RU"
									"zh-CN"
								]
							) {
								customerStoryCollection(limit: 1) {
									total
								}
							}
						}
					}
				}
			}
		}
		taxonomyRegionCollection(locale: $locale, limit: 10) {
			total
			items {
				regionNameExternal
				linkedFrom(
					allowedLocales: [
						"en-US"
						"es"
						"ja-JP"
						"pt-BR"
						"fr-FR"
						"de-DE"
						"nl-NL"
						"it-IT"
						"ko-KR"
						"pl-PL"
						"ru-RU"
						"zh-CN"
					]
				) {
					companyNameInternalCollection {
						total
						items {
							linkedFrom(
								allowedLocales: [
									"en-US"
									"es"
									"ja-JP"
									"pt-BR"
									"fr-FR"
									"de-DE"
									"nl-NL"
									"it-IT"
									"ko-KR"
									"pl-PL"
									"ru-RU"
									"zh-CN"
								]
							) {
								customerStoryCollection(limit: 1) {
									total
								}
							}
						}
					}
				}
			}
		}
		taxonomySolutionCollection(locale: $locale, limit: 20) {
			total
			items {
				solutionNameExternal
				linkedFrom(
					allowedLocales: [
						"en-US"
						"es"
						"ja-JP"
						"pt-BR"
						"fr-FR"
						"de-DE"
						"nl-NL"
						"it-IT"
						"ko-KR"
						"pl-PL"
						"ru-RU"
						"zh-CN"
					]
				) {
					customerStoryCollection {
						total
					}
				}
			}
		}
		taxonomyCompanySizeCollection(locale: $locale, limit: 5) {
			total
			items {
				companySizeTitleExternal
				linkedFrom(
					allowedLocales: [
						"en-US"
						"es"
						"ja-JP"
						"pt-BR"
						"fr-FR"
						"de-DE"
						"nl-NL"
						"it-IT"
						"ko-KR"
						"pl-PL"
						"ru-RU"
						"zh-CN"
					]
				) {
					companyNameInternalCollection {
						total
						items {
							linkedFrom(
								allowedLocales: [
									"en-US"
									"es"
									"ja-JP"
									"pt-BR"
									"fr-FR"
									"de-DE"
									"nl-NL"
									"it-IT"
									"ko-KR"
									"pl-PL"
									"ru-RU"
									"zh-CN"
								]
							) {
								customerStoryCollection(limit: 1) {
									total
								}
							}
						}
					}
				}
			}
		}
	}
`;

/**
 * Returns variables for Contentful Anchor Pages query
 */
export const getCustomerLandingPageRelayQueryVars = (location: Location) => {
	const { locale } = parseRoute(location);

	return {
		locale: WAC_TO_CPAPI_LOCALE_MAP[locale] || 'en-US',
	};
};

/**
 * Transforms Contentful Response to shape matching CPAPI response/props
 */
export const CustomerLandingPageContentfulResponse = (
	contentfulStories: customerLandingPageRelayQuery$data,
) => {
	return {
		customerStories: {
			edges:
				contentfulStories.customerStoryCollection?.items?.map((item) => {
					return {
						node: {
							title: item?.storyTitleExternal,
							publishedAt: item?.sys?.publishedAt ?? null,
							customerCompany: {
								name: item?.customerCompany?.organizationNameExternal,
								logo: item?.customerCompany?.logoCollection?.items?.map((item) => {
									return {
										description: item?.description,
										contentType: item?.contentType,
										url: item?.url,
									};
								}),
								industry: item?.customerCompany?.industryCollection?.items?.map((item) => {
									return {
										name: item?.industryName,
									};
								}),
								region: {
									name: item?.customerCompany?.region?.regionNameExternal,
								},
								organizationSize: {
									name: item?.customerCompany?.organizationSize?.companySizeTitleExternal,
								},
							},
							advocateQuotes: item?.advocateQuotesCollection?.items
								?.map((item) => {
									if (
										item?.advocateName &&
										item.advocateQuote &&
										item.advocateJobTitle &&
										item.advocateHeadshot
									) {
										return {
											advocateName: item?.advocateName,
											advocateQuote: item?.advocateQuote,
											advocateJobTitle: item?.advocateJobTitle,
											advocateHeadshot: item?.advocateHeadshot,
										};
									}

									return null;
								})
								.filter((item) => item !== null),
							storyIcon: item?.storyIcon,
							callToAction: item?.callToActionCollection?.items?.map((item) => {
								return {
									text: item?.ctaText,
									url: item?.ctaUrl,
								};
							}),
							products: item?.productCollection?.items?.map((item) => {
								return {
									name: item?.productNameReference?.productName,
								};
							}),
							marketplaceApps: item?.marketplaceAppsCollection?.items?.map((item) => {
								return {
									name: item?.title,
								};
							}),
							solutions: item?.solutionCollection?.items?.map((item) => {
								return {
									name: item?.solutionNameExternal,
								};
							}),
							solutionPartners: item?.solutionPartnersCollection?.items?.map((item) => {
								return {
									name: item?.organizationNameExternal,
								};
							}),
							urlSlug: item?.urlSlug,
						},
					};
				}) ?? [],
		},
		taxonomyCompanySizeCollection: contentfulStories.taxonomyCompanySizeCollection,
		filters: {
			edges: [
				...(contentfulStories?.taxonomyIndustryCollection?.items
					?.filter((item) =>
						item?.linkedFrom?.companyNameInternalCollection?.items?.some(
							(link) => link?.linkedFrom?.customerStoryCollection?.total,
						),
					)
					.map((element) => {
						return {
							node: {
								context: {
									name: element?.industryName,
								},
								field: 'industry',
							},
						};
					}) ?? []),
				...(contentfulStories?.taxonomyCompanySizeCollection?.items
					?.filter((item) =>
						item?.linkedFrom?.companyNameInternalCollection?.items?.some(
							(link) => link?.linkedFrom?.customerStoryCollection?.total,
						),
					)
					.map((element) => {
						return {
							node: {
								context: {
									name: element?.companySizeTitleExternal,
								},
								field: 'organizationSize',
							},
						};
					}) ?? []),
				...(contentfulStories?.productNameCollection?.items
					?.filter((item) =>
						item?.linkedFrom?.productListingCollection?.items?.some(
							(link) => link?.linkedFrom?.customerStoryCollection?.total,
						),
					)
					.map((element) => {
						return {
							node: {
								context: {
									name: element?.productName,
								},
								field: 'products',
							},
						};
					}) ?? []),
				...(contentfulStories?.taxonomyRegionCollection?.items
					?.filter((item) =>
						item?.linkedFrom?.companyNameInternalCollection?.items?.some(
							(link) => link?.linkedFrom?.customerStoryCollection?.total,
						),
					)
					.map((element) => {
						return {
							node: {
								context: {
									name: element?.regionNameExternal,
								},
								field: 'region',
							},
						};
					}) ?? []),
				...(contentfulStories?.taxonomySolutionCollection?.items
					?.filter((item) => item?.linkedFrom?.customerStoryCollection?.total)
					.map((element) => {
						return {
							node: {
								context: {
									name: element?.solutionNameExternal,
								},
								field: 'solution',
							},
						};
					}) ?? []),
			],
		},
	};
};
